"use client";

import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-0">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="px-4 py-2 text-sm font-mono tracking-widest uppercase text-foreground hover:text-secondary transition-all duration-400 relative group">
                {item.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-secondary group-hover:w-full transition-all duration-400" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2">
        <div className="h-6 w-24 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-24 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-24 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <nav className="flex flex-col gap-4">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="text-sm font-mono tracking-widest uppercase text-foreground hover:text-secondary transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-400 ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border" : "bg-background"}`}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-12 bg-secondary" />

      <div className="container mx-auto px-4 md:px-6 py-4">
        <div className="grid grid-cols-12 gap-6 items-center">
          {}
          <div className="col-span-12 md:col-span-3">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-8 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex col-span-9 justify-end">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:hidden col-span-12 flex justify-end">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-background border-l border-border">
                <div className="mt-8">
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}